import React from 'react'
import { captureException } from '@sentry/react'
import { InferType } from 'yup'
import { useMutation } from '@tanstack/react-query'
import { Modal } from '../../../../../components/common/Modal'
import { ModalHeader } from '../../../../../components/common/Modal/ModalHeader'
import { ModalBody } from '../../../../../components/common/Modal/ModalBody'
import { useToastNotifications } from '../../../../../hooks/useToastNotification'
import { ToastType } from '../../../../../components/common/Toast/toast-type'
import { useAppTranslation } from '../../../../../hooks/useAppTranslation'
import { EditSocietyInvoiceModalContent } from './EditSocietyInvoiceModalContent'
import { formatOrganisationNumber } from '../../../../../helpers/organisation_number'
import { useAdminSociety } from '../useAdminSociety'
import { adminInvoiceDetailsSchema } from '../../../../../forms/schemas/admin_edit_invoice_details'
import { patchSociety } from '../../../../../api/admin'

interface EditSocietyModalProps {
  show: boolean
  close: () => void
  societyId?: string
}

export const EditSocietyInvoiceModal = ({
  show,
  close,
  societyId,
}: EditSocietyModalProps): React.ReactNode => {
  const { setToastNotification } = useToastNotifications()
  const { translate } = useAppTranslation()
  const { adminSociety } = useAdminSociety(societyId)

  const initialOrganisationNumber = adminSociety?.organisationNumber

  const { mutate } = useMutation({
    mutationFn: patchSociety,
    onSuccess: () => {
      setToastNotification(
        ToastType.SUCCESS,
        translate('Föreningens faktureringsuppgifter uppdaterade')
      )
      close()
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('flashMessage.somethingWentWrongError')
      )
    },
  })

  const onSubmit = async (
    data: InferType<typeof adminInvoiceDetailsSchema>
  ): Promise<void> => {
    const { organisationNumber, ...rest } = data
    // Avoid sending organisationNumber if it hasn't been changed, backend doesn't allow already existing
    // organisation numbers
    const dataToSend =
      initialOrganisationNumber &&
      organisationNumber &&
      formatOrganisationNumber(`${initialOrganisationNumber}`) ===
        formatOrganisationNumber(`${organisationNumber}`)
        ? rest
        : data

    societyId && mutate({ societyId, data: dataToSend })
  }

  const onError = (error: unknown): void => {
    captureException(error)
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  return (
    <Modal show={show} size="xl">
      {{
        header: (
          <ModalHeader onClose={close}>
            Uppdatera fakturering för förening
          </ModalHeader>
        ),
        body: (
          <ModalBody className="overflow-y-auto">
            {adminSociety && (
              <EditSocietyInvoiceModalContent
                onSubmit={onSubmit}
                onError={onError}
                onClose={close}
                society={adminSociety}
              />
            )}
          </ModalBody>
        ),
      }}
    </Modal>
  )
}
